import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/adminService';
import type { AdminUserListItem } from '../types/admin';

interface UserSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Format relative time
const formatRelativeTime = (dateString: string | null): string => {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
};

export const UserSearchModal: React.FC<UserSearchModalProps> = ({ isOpen, onClose }) => {
  const { impersonateUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setDebouncedQuery('');
      setSelectedUserId(null);
      setIsImpersonating(false);
    }
  }, [isOpen]);

  // Search users query
  const {
    data: users,
    isLoading: isSearching,
    error: searchError,
  } = useQuery({
    queryKey: ['adminUserSearch', debouncedQuery],
    queryFn: () => adminService.searchUsers(debouncedQuery),
    enabled: isOpen && debouncedQuery.length > 0,
    staleTime: 30000, // 30 seconds
  });

  const [impersonationError, setImpersonationError] = useState<string | null>(null);

  // Handle user selection and impersonation
  const handleSelectUser = async (user: AdminUserListItem) => {
    setSelectedUserId(user.id);
    setIsImpersonating(true);
    setImpersonationError(null);

    try {
      const impersonationData = await adminService.getUserForImpersonation(user.id);

      // Validate that we got a token
      if (!impersonationData.token) {
        throw new Error('Backend did not return a token for impersonation');
      }

      impersonateUser(impersonationData);
      onClose();
    } catch (error: any) {
      console.error('Failed to impersonate user:', error);
      const errorMessage = error.response?.data?.detail
        || error.response?.data?.message
        || error.message
        || 'Failed to impersonate user';
      setImpersonationError(errorMessage);
      setIsImpersonating(false);
      setSelectedUserId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-dark-surface border border-dark-border rounded-lg w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border">
          <div className="flex items-center gap-2">
            {/* Eye icon */}
            <svg className="w-5 h-5 text-accent-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            <h2 className="text-lg font-semibold text-gray-100">Impersonate User</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-300 transition-colors"
            disabled={isImpersonating}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search Input */}
        <div className="px-6 py-4 border-b border-dark-border">
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by username or email..."
              className="w-full px-4 py-2 pl-10 bg-dark-bg border border-dark-border rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:border-accent-yellow transition-colors"
              disabled={isImpersonating}
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Error Message */}
        {impersonationError && (
          <div className="px-6 py-3 bg-bear-red/10 border-b border-bear-red/20">
            <p className="text-sm text-bear-red">{impersonationError}</p>
          </div>
        )}

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {!debouncedQuery && (
            <div className="px-6 py-8 text-center text-gray-500 text-sm">
              Start typing to search for users
            </div>
          )}

          {debouncedQuery && isSearching && (
            <div className="px-6 py-8 text-center">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-gray-500 border-t-accent-yellow"></div>
              <p className="mt-2 text-gray-500 text-sm">Searching...</p>
            </div>
          )}

          {debouncedQuery && searchError && (
            <div className="px-6 py-8 text-center text-bear-red text-sm">
              Failed to search users. Please try again.
            </div>
          )}

          {debouncedQuery && !isSearching && users && users.length === 0 && (
            <div className="px-6 py-8 text-center text-gray-500 text-sm">
              No users found matching "{debouncedQuery}"
            </div>
          )}

          {users && users.length > 0 && (
            <div className="divide-y divide-dark-border">
              {users.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleSelectUser(user)}
                  disabled={isImpersonating}
                  className="w-full px-6 py-3 text-left hover:bg-dark-surface-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-100">{user.username}</span>
                        {!user.is_active && (
                          <span className="px-1.5 py-0.5 text-xs bg-bear-red/20 text-bear-red rounded">
                            Inactive
                          </span>
                        )}
                        {user.is_staff && (
                          <span className="px-1.5 py-0.5 text-xs bg-accent-yellow/20 text-accent-yellow rounded">
                            Staff
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 truncate">
                        {user.email || 'No email'}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Last login</div>
                        <div className="text-xs text-gray-400">
                          {formatRelativeTime(user.last_login)}
                        </div>
                      </div>
                      {selectedUserId === user.id && isImpersonating ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-500 border-t-accent-yellow"></div>
                      ) : (
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-dark-border">
          <p className="text-xs text-gray-500">
            Select a user to view the app as they would see it. Refresh the page to exit impersonation mode.
          </p>
        </div>
      </div>
    </div>
  );
};
